import type { PresentFramesRequest } from "@/lib/canvas-presentation-types";
import type { FocusFrameTag, PresentationState } from "@/lib/focus/focus-types";
import {
  isUserKept,
  type FocusRoundOwner,
} from "@/lib/focus/focus-plan-rounds";
import { usePresentationStore } from "@/stores/presentation-store";

type OriginRequest = Pick<
  PresentFramesRequest,
  "agentId" | "spaceId" | "executionId" | "ownerId"
>;

/** The agent that presented a focus, and its round (onboarding and edits read it). */
export function focusOriginOf(
  request: OriginRequest,
): PresentationState["origin"] {
  return request.agentId
    ? {
        agentId: request.agentId,
        spaceId: request.spaceId ?? null,
        round: request.executionId ?? null,
      }
    : undefined;
}

/**
 * Whose answers a presentation's frames belong to (plan 13): the backend's
 * owner (conversation, task or execution), else its execution,
 * else the presenting agent. Undefined when nobody but the user presents.
 */
export function focusOwnerKey(
  request: Pick<OriginRequest, "agentId" | "executionId" | "ownerId">,
): string | undefined {
  return request.ownerId || request.executionId || request.agentId || undefined;
}

export function focusRoundOwnerOf(
  request: OriginRequest,
): FocusRoundOwner | null {
  const ownerId = focusOwnerKey(request);
  return ownerId ? { ownerId, round: request.executionId ?? null } : null;
}

/**
 * A focus an agent just entered or refitted: its frames are that round's,
 * except those `before` already showed as the user's.
 */
export function stampAgentRound(
  request: OriginRequest,
  before: PresentationState | null,
): void {
  const owner = focusRoundOwnerOf(request);
  usePresentationStore.setState((state) => {
    const presentation = state.presentation;
    if (!presentation || !owner) return state;
    const usersBefore = (id: string) =>
      before?.variant === "focus" &&
      before.frameIds.includes(id) &&
      isUserKept(before.frameTags?.[id]);
    const frameTags: Record<string, FocusFrameTag> = Object.fromEntries(
      presentation.frameIds.flatMap((id) => {
        const tag = presentation.frameTags?.[id] ?? before?.frameTags?.[id];
        if (usersBefore(id)) return tag ? [[id, tag]] : [];
        return [[id, { ...tag, owner: owner.ownerId, round: owner.round }]];
      }),
    );
    return {
      presentation: {
        ...presentation,
        origin: focusOriginOf(request),
        frameTags,
      },
    };
  });
}
